import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, increment, runTransaction } from 'firebase/firestore';
import { db } from '../App';
import useBank from './useBank';
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import ItemModal from './ItemModal';
import { FaShoppingCart, FaDollarSign } from 'react-icons/fa';
import './Shop.css';

const Shop = () => {
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalPosition, setModalPosition] = useState(null);
  const [isSellSection, setIsSellSection] = useState(false);
  const { purchaseItem, removeItem } = useBank();
  const { user, updateUser } = useFireAuth();

  const categoryOrder = [
    'Scratchcards',
    'Merch',
    'Miners',
    'Miscellaneous',
  ];

  useEffect(() => {
    const fetchItems = async () => {
      const itemsCollection = collection(db, 'items');
      const q = query(itemsCollection, where(isSellSection ? 'sellable' : 'forSale', '==', true));
      const itemsSnapshot = await getDocs(q);
      const itemsList = itemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setItems(itemsList);
    };

    fetchItems();
  }, [isSellSection]);

  const handlePurchase = async (item) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setFeedback({
      type: 'processing',
      message: `Processing purchase of ${item.name}...`,
      itemId: item.id,
    });

    try {
      const purchasedItemId = await purchaseItem(item);

      setFeedback({
        type: 'success',
        message: `Purchase Successful`,
        itemId: item.id,
      });
      console.log(`Successfully purchased ${item.name} with ID ${purchasedItemId}`);

      await updateUser();
    } catch (error) {
      setFeedback({
        type: 'error',
        message: `Failed to purchase ${item.name}: ${error.message}`,
        itemId: item.id,
      });
      console.error('Failed to purchase item:', error);
    }

    setTimeout(() => {
      setFeedback(null);
      setIsProcessing(false);
    }, 1500);
  };

  const handleSell = async (item) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setFeedback({
      type: 'processing',
      message: `Processing sale of ${item.name}...`,
      itemId: item.id,
    });

    try {
      const itemIdToRemove = user.inventory.find((invItem) => invItem.split('_')[1] === item.name);

      if (!itemIdToRemove) {
        throw new Error(`You don't own any ${item.name}`);
      }

      const sold = await removeItem(itemIdToRemove);

      if (!sold) {
        throw new Error(`Failed to sell ${item.name}`);
      }

      const userRef = doc(db, 'users', user.uid);
      const bankRef = doc(db, 'bank', 'globalBank');
      const isMoney = item.currency === 'money';
      const price = item.sellPrice;

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          throw new Error('User document does not exist!');
        }

        const bankDoc = await transaction.get(bankRef);
        if (!bankDoc.exists()) {
          throw new Error('Bank document does not exist!');
        }

        const newBalance = increment(price);
        const bankNewBalance = increment(-price);

        const updateData = {
          [isMoney ? 'money' : 'crypto.hahumCoin']: newBalance,
        };

        transaction.update(userRef, updateData);
        transaction.update(bankRef, {
          [isMoney ? 'money' : 'hahumCoin']: bankNewBalance,
        });
      });

      await updateUser();

      setFeedback({
        type: 'success',
        message: `Sale Successful`,
        itemId: item.id,
      });

      console.log(`Successfully sold ${item.name} with ID ${itemIdToRemove}`);
    } catch (error) {
      setFeedback({
        type: 'error',
        message: `Failed to sell ${item.name}: ${error.message}`,
        itemId: item.id,
      });
      console.error('Failed to sell item:', error);
    }

    setTimeout(() => {
      setFeedback(null);
      setIsProcessing(false);
    }, 1500);
  };

  const handleOpenModal = (item, event) => {
    if (isSellSection) return; // Disable modal for sell section
    const rect = event.target.getBoundingClientRect();
    setModalPosition({ top: rect.top + window.scrollY });
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setModalPosition(null);
  };

  const getItemCount = (itemName) => {
    return user?.inventory?.filter((item) => item.split('_')[1] === itemName).length || 0;
  };

  const categories = items.reduce((acc, item) => {
    const category = item.category || 'Miscellaneous';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  const sortedCategories = Object.keys(categories).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    if (indexA === -1 && indexB === -1) {
      return a.localeCompare(b);
    }
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <div className="shop-section">
      <div className="shop-container">
        <div className="shop-header">
          <h2 className="shop-title"><FaShoppingCart /> {isSellSection ? 'Sell' : 'Shop'}</h2>
          <button
            onClick={() => setIsSellSection(!isSellSection)}
            className="toggle-section-button"
          >
            {isSellSection ? <><FaShoppingCart /> Shop</> : <><FaDollarSign /> Sell</>}
          </button>
        </div>
        <div className="balance-card">
          <h3>Balance</h3>
          <div className="balance">
            <div className="balance-item">💴 {user?.money !== undefined ? user.money.toFixed(2) : 'Loading...'}</div>
            <div className="balance-item">🪙 {user?.crypto?.hahumCoin !== undefined ? user.crypto.hahumCoin.toFixed(2) : 'Loading...'}</div>
          </div>
        </div>
        {sortedCategories.map((category) => (
          <div key={category}>
            <h3 className="category-title">{category}</h3>
            <div className="shop-items-grid">
              {categories[category].map(item => (
                <div key={item.id} className="shop-item-card" onClick={(e) => handleOpenModal(item, e)}>
                  <img src={item.imageUrl} alt={item.name} className="shop-item-image" />
                  <h3>{item.name}</h3>
                  {isSellSection ? (
                    <>
                      <p>Sell Price: {item.currency === 'money' ? '💴' : '🪙'} {item.sellPrice}</p>
                      <p className="shop-item-quantity">Owned: {getItemCount(item.name)}</p>
                    </>
                  ) : (
                    <p>Price: {item.currency === 'money' ? '💴' : '🪙'} {item.price}</p>
                  )}
                  {isSellSection ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSell(item); }}
                      disabled={getItemCount(item.name) === 0 || isProcessing}
                      className="shop-sell-button"
                    >
                      {isProcessing && feedback?.itemId === item.id ? 'Processing...' : 'Sell'}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePurchase(item); }}
                      disabled={(item.currency === 'money' ? user?.money < item.price : user?.crypto?.hahumCoin < item.price) || isProcessing}
                      className="shop-buy-button"
                    >
                      {isProcessing && feedback?.itemId === item.id ? 'Processing...' : 'Buy'}
                    </button>
                  )}
                  {feedback && feedback.itemId === item.id && (
                    <div className={`shop-feedback ${feedback.type}`}>
                      {feedback.message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {selectedItem && !isSellSection && (
        <ItemModal
          item={selectedItem}
          onClose={handleCloseModal}
          onPurchase={handlePurchase}
          isProcessing={isProcessing}
          feedback={feedback}
          position={modalPosition}
        />
      )}
    </div>
  );
};

export default DelayRender(Shop, 300);
